import React from "react";
import { IBranch } from "../../models/Branch";
import ModalContainer from "../ModalContainer.tsx";
import dayjs from "dayjs";
import {
  autocompletePartners,
  readPartnersByBranch,
} from "../../services/partner.service.ts";
import {
  findChartDaysWorked,
  findPaymentDetail,
  findPayrollSummary,
  findTotalIncidents,
} from "../../services/payroll.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import * as XLSX from "xlsx-js-style";
import { generateReciboSheet } from "../../utils/generateReciboExcel.ts";
import jsPDF from "jspdf";
import { getCurrencyFormat } from "../../utils/index.ts";
import {
  applyRejectedIncidentsZeroHorasExtras,
  appendReciboFreeDaysEnjoyedRows,
  buildAbsenceOtrosIngresoRows,
  buildReciboDailyPaymentsFromPaymentDetail,
  collectReciboDeductionLines,
  getReciboJornadasCompletasCuadroDisplay,
  groupDeductionsForRecibo,
  isReciboRestDayEnjoyedRow,
  isReciboRestDayWorkedRow,
  mergeAbsenceRowsForRecibo,
  countDailyPaymentsWithAcceptedExtras,
  sumExtraHoursFromDailyPayments,
  summarizeChartDays,
} from "../../utils/payrollUtils.ts";
import * as moment from "moment-timezone";
import {
  calculateDailyPayment,
} from "../../services/payroll/ui.ts";
import { calcularTiempoTrabajado } from "../../interfaces/formik/IReporteRelojChecador.ts";
import { useFullDayHours } from "../../hooks/useFullDayHours.tsx";

interface Partner {
  id: number;
  person: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
  };
  candidate?: {
    vacancy?: {
      position?: {
        name: string;
      };
    };
  };
}

interface BulkRecibosModalProps {
  open: boolean;
  onClose: () => void;
  branchSelected: IBranch | null;
  dates: { start: dayjs.Dayjs; end: dayjs.Dayjs };
  specialPartners?: Array<{ record: any; rawRecord: any; customDates: { start: dayjs.Dayjs; end: dayjs.Dayjs } }>;
}

/** Misma fuente que PaymentDetail (resumen de nómina) si payment-detail viene sin líneas. */
function mergeReciboDeductionArraysFromSummary(
  paymentDetailResponse: any,
  payrollRecord: any
): { calculatedNewDeductions: any[]; calculatedMissingBoxes: any[] } {
  const fromDetailNew = paymentDetailResponse?.calculatedNewDeductions;
  const calculatedNewDeductions =
    Array.isArray(fromDetailNew) && fromDetailNew.length > 0
      ? fromDetailNew
      : payrollRecord?.calculatedNewDeductions || [];

  const fromDetailMb = paymentDetailResponse?.calculatedMissingBoxes;
  const calculatedMissingBoxes =
    Array.isArray(fromDetailMb) && fromDetailMb.length > 0
      ? fromDetailMb
      : payrollRecord?.calculatedMissingBoxes || [];

  return { calculatedNewDeductions, calculatedMissingBoxes };
}

/** Misma fuente de datos que PaymentDetail para exportar recibo (Excel/PDF). */
async function loadPartnerReciboExportData(
  partnerId: number,
  branchId: number,
  effectiveStart: string,
  effectiveEnd: string,
  payrollRecord: any,
  fullDayHours: number
): Promise<{ partnerData: any; paymentDetailData: any }> {
  const [paymentDetailResponse, chartResponse, incidentsResponse] =
    await Promise.all([
      findPaymentDetail(
        partnerId,
        branchId,
        effectiveStart,
        effectiveEnd,
        undefined,
        { refresh: true }
      ),
      findChartDaysWorked(partnerId, branchId, effectiveStart, effectiveEnd).catch(
        () => ({
          freeDaysDates: [],
          detailed: [],
          graph: [],
          savedIncidents: [],
        })
      ),
      findTotalIncidents(partnerId, branchId, effectiveStart, effectiveEnd).catch(
        () => ({ incidents: [] })
      ),
    ]);

  const pd = paymentDetailResponse?.paymentDetails || {};
  const savedIncidents =
    incidentsResponse?.incidents || chartResponse?.savedIncidents || [];
  const dailySalary = Number(payrollRecord.dailySalary);
  const hourlyPay = Number(payrollRecord.hourlyPay);
  const NormalCalculationNomina = payrollRecord.NormalCalculationNomina || false;
  const freeDaysEnjoyedDates =
    paymentDetailResponse?.freeDaysEnjoyedDates || [];

  const dailyPayments = appendReciboFreeDaysEnjoyedRows(
    buildReciboDailyPaymentsFromPaymentDetail({
      backendDailyPayments: paymentDetailResponse.dailyPayments || [],
      dailySalary,
      hourlyPay,
      NormalCalculationNomina,
      fullDayHours,
      chartFreeDaysDates: chartResponse?.freeDaysDates || [],
      freeDaysEnjoyedDates,
      savedIncidents,
    }),
    freeDaysEnjoyedDates,
    dailySalary
  );

  const partnerData = {
    partnerId: payrollRecord.partnerId,
    name: payrollRecord.name,
    position: payrollRecord.position,
    branch: payrollRecord.branch,
    salary: `$${payrollRecord.salaryPerFortnight.toFixed(2)}`,
    dailySalary: `$${payrollRecord.dailySalary.toFixed(2)}`,
    payPerHour: `$${payrollRecord.hourlyPay.toFixed(2)}`,
    totalIncentives:
      pd.totalIncentives || payrollRecord.totalIncentives || "$0.00",
    totalDeductions:
      pd.totalDeductions || payrollRecord.totalDeductions || "$0.00",
    uniformDeduction: payrollRecord.uniformDeduction,
    totalHollydays: payrollRecord.totalHollydays ?? 0,
    celebrityDaysWorked: payrollRecord.celebrityDaysWorked || 0,
    celebrityDaysPayTotal: payrollRecord.celebrityDaysPayTotal || "$0.00",
    freeDaysWorked: payrollRecord.freeDaysWorked || 0,
    freeDaysEnjoyed: payrollRecord.freeDaysEnjoyed || 0,
    NormalCalculationNomina,
  };

  const paymentDetailData = {
    dailyPayments,
    absenceRows: paymentDetailResponse.absenceRows || [],
    paymentDetails: {
      ...pd,
      hoursAuthorizedPay: pd.hoursAuthorizedPay || "$0.00",
      doubleShiftPayTotal: pd.doubleShiftPayTotal || "$0.00",
      extraHourPayTotal: pd.extraHourPayTotal || "$0.00",
      freeDayWorkedPayTotal: pd.freeDayWorkedPayTotal || "$0.00",
      freeDaysEnjoyedPay: pd.freeDaysEnjoyedPay || "$0.00",
      totalIncentives: partnerData.totalIncentives,
      totalDeductions: partnerData.totalDeductions,
      deductionBreakdown: pd.deductionBreakdown || [],
      freeDaysWorked: pd.freeDaysWorked ?? payrollRecord.freeDaysWorked ?? 0,
      freeDaysEnjoyed:
        pd.freeDaysEnjoyed ?? payrollRecord.freeDaysEnjoyed ?? 0,
    },
    schedules: paymentDetailResponse.schedules || [],
    ...mergeReciboDeductionArraysFromSummary(
      paymentDetailResponse,
      payrollRecord
    ),
  };

  return { partnerData, paymentDetailData };
}

/** OOXML: nombre de hoja ≤31 caracteres; no puede incluir : \\ / ? * [ ] */
function buildUniqueExcelSheetName(
  rawName: unknown,
  partnerId: number,
  used: Set<string>
): string {
  const cleaned = String(rawName ?? "")
    .replace(/[:\\/*?\[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .substring(0, 31);
  let base = cleaned || `P${partnerId}`;
  let candidate = base;
  let n = 0;
  while (used.has(candidate)) {
    n += 1;
    const suffix = ` (${n})`;
    const maxBase = Math.max(1, 31 - suffix.length);
    candidate = base.substring(0, maxBase) + suffix;
  }
  used.add(candidate);
  return candidate;
}

const BulkRecibosModal: React.FC<BulkRecibosModalProps> = ({
  open,
  onClose,
  branchSelected,
  dates,
  specialPartners,
}) => {
  const [partners, setPartners] = React.useState<Partner[]>([]);
  const [filteredPartners, setFilteredPartners] = React.useState<Partner[]>([]);
  const [selectedPartnersForRecibo, setSelectedPartnersForRecibo] =
    React.useState<number[]>([]);
  const [selectAllMode, setSelectAllMode] = React.useState<boolean>(false);
  const [searchPartner, setSearchPartner] = React.useState<string>("");
  const [showPartnerDropdown, setShowPartnerDropdown] = React.useState(false);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [exportFormat, setExportFormat] = React.useState<"excel" | "pdf">(
    "pdf"
  );
  const searchContainerRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutIdRef = React.useRef<any>(null);
  
  const { fullDayHours: fullDayHoursFromHook } = useFullDayHours(
    branchSelected ? Number(branchSelected.id) : 0
  );
  const fullDayHours = React.useMemo(() => {
    const value =
      typeof fullDayHoursFromHook === "number" &&
      !isNaN(fullDayHoursFromHook) &&
      fullDayHoursFromHook > 0
        ? fullDayHoursFromHook
        : 8.5;
    return value;
  }, [fullDayHoursFromHook]);

  const fetchPartners = async (searchTerm: string = "", branchId?: number) => {
    try {
      setSearchLoading(true);
      const resp = await autocompletePartners(searchTerm, branchId);
      const partnersList = resp || [];
      setPartners(partnersList);
      setFilteredPartners(partnersList);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectAllPartners = async () => {
    const allFilteredSelected = filteredPartners.every((partner) =>
      selectedPartnersForRecibo.includes(partner.id)
    );

    if (allFilteredSelected) {
      const filteredPartnerIds = filteredPartners.map((partner) => partner.id);
      setSelectedPartnersForRecibo(
        selectedPartnersForRecibo.filter(
          (id) => !filteredPartnerIds.includes(id)
        )
      );
      setSelectAllMode(false);
    } else {
      if (!branchSelected) {
        notification(
          "Error",
          "Debes seleccionar una sucursal primero",
          "warning"
        );
        return;
      }

      try {
        const allBranchPartners = await readPartnersByBranch(
          Number(branchSelected.id)
        );
        const partnerIds = allBranchPartners.map((p) => p.id);

        const existingPartners = new Map(partners.map((p) => [p.id, p]));
        allBranchPartners.forEach((partner: Partner) => {
          existingPartners.set(partner.id, partner);
        });
        setPartners(Array.from(existingPartners.values()));

        const newSelected = [...selectedPartnersForRecibo];
        partnerIds.forEach((id) => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });

        setSelectAllMode(true);
        setSelectedPartnersForRecibo(newSelected);

        notification(
          "Éxito",
          `${partnerIds.length} colaboradores de la sucursal seleccionados`,
          "success"
        );
      } catch (error) {
        notification(
          "Error",
          "No se pudieron cargar los colaboradores de la sucursal",
          "danger"
        );
      }
    }
  };

  const handlePartnerToggle = async (partner: Partner) => {
    const isSelected = selectedPartnersForRecibo.includes(partner.id);

    if (isSelected) {
      setSelectedPartnersForRecibo(
        selectedPartnersForRecibo.filter((id) => id !== partner.id)
      );
    } else {
      setSelectedPartnersForRecibo([...selectedPartnersForRecibo, partner.id]);
    }
    setSelectAllMode(false);
    setSearchPartner("");

    if (branchSelected) {
      try {
        const allBranchPartners = await readPartnersByBranch(
          Number(branchSelected.id)
        );

        const defaultPartners = allBranchPartners
          .filter((p) => !selectedPartnersForRecibo.includes(p.id))
          .slice(0, 10);
        setFilteredPartners(defaultPartners);

        const existingPartners = new Map(partners.map((p) => [p.id, p]));
        allBranchPartners.forEach((partner: Partner) => {
          existingPartners.set(partner.id, partner);
        });
        setPartners(Array.from(existingPartners.values()));

        setShowPartnerDropdown(true);
      } catch (error) {
        console.error("Error loading default partners:", error);
        setShowPartnerDropdown(false);
      }
    } else {
      setShowPartnerDropdown(false);
    }
  };

  const handlePartnerSearch = async (searchTerm: string) => {
    setSearchPartner(searchTerm);
    setShowPartnerDropdown(true);

    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

    timeoutIdRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        if (searchTerm.trim() === "" && branchSelected) {
          const allBranchPartners = await readPartnersByBranch(
            Number(branchSelected.id)
          );
          const defaultPartners = allBranchPartners
            .filter((p) => !selectedPartnersForRecibo.includes(p.id))
            .slice(0, 10);
          setFilteredPartners(defaultPartners);

          const existingPartners = new Map(partners.map((p) => [p.id, p]));
          allBranchPartners.forEach((partner: Partner) => {
            existingPartners.set(partner.id, partner);
          });
          setPartners(Array.from(existingPartners.values()));
        } else {
          const data = await autocompletePartners(
            searchTerm,
            branchSelected ? Number(branchSelected.id) : undefined
          );
          setFilteredPartners(data);

          const existingPartners = new Map(partners.map((p) => [p.id, p]));
          data.forEach((partner: Partner) => {
            if (!existingPartners.has(partner.id)) {
              existingPartners.set(partner.id, partner);
            }
          });
          setPartners(Array.from(existingPartners.values()));
        }
      } catch (error) {
        console.error("Error searching partners:", error);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  };

  React.useEffect(() => {
    if (open && branchSelected) {
      fetchPartners("", Number(branchSelected.id));

      const selectAllOnOpen = async () => {
        try {
          const allBranchPartners = await readPartnersByBranch(
            Number(branchSelected.id)
          );
          const partnerIds = allBranchPartners.map((p) => p.id);

          setPartners(allBranchPartners);
          setFilteredPartners(allBranchPartners.slice(0, 10));

          setSelectedPartnersForRecibo(partnerIds);
          setSelectAllMode(true);
        } catch (error) {
          console.error("Error loading partners on modal open:", error);
        }
      };
      selectAllOnOpen();
    } else if (!open) {
      setPartners([]);
      setFilteredPartners([]);
      setSelectedPartnersForRecibo([]);
      setSelectAllMode(false);
    }
  }, [open, branchSelected]);

  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      const el = searchContainerRef.current as any;
      if (
        el &&
        !el.contains(event.target) &&
        !event.target.closest(".partner-dropdown-item") &&
        !event.target.closest('input[type="checkbox"]') &&
        !event.target.closest("button")
      ) {
        setShowPartnerDropdown(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Función para calcular el total de días festivos trabajados desde dailyPayments
  // Igual que getCelebrityDaysTotalFromBreakdown en PaymentDetail.tsx
  const calculateCelebrityDaysTotalFromBreakdown = (
    dailyPayments: any[],
    dailySalary: number,
    hourlyPay: number,
    NormalCalculationNomina: boolean,
    fullDayHours: number
  ): number => {
    if (!dailyPayments || dailyPayments.length === 0) {
      return 0;
    }

    let total = 0;
    dailyPayments.forEach((dp: any) => {
      if (dp.isCelebrityDayWorked && dp.celebrityDay) {
        const isNewSystem = dp.celebrityDay?.isNewSystem || false;
        
        if (isNewSystem && dp.celebrityDay.amount) {
          // Para nuevos bonos, usar el monto directamente
          total += Number(dp.celebrityDay.amount) || 0;
        } else {
          // Para legacy, calcular basándose en bonusType
          let hoursInSchedule = dp.hoursInSchedule || 0;
          const totalHours = dp.hours || 0;
          
          // Si no hay hoursInSchedule, calcularlo desde el schedule
          if (hoursInSchedule === 0 && dp.schedule?.startTime && dp.schedule?.endTime && dp.checkIn && dp.checkOut) {
            const [start] = dp.schedule.startTime.split(" - ");
            const [, end] = dp.schedule.endTime.split(" - ");
            const entradaProgramada = start + ":00";
            const salidaProgramada = end + ":00";
            const entradaRegistrada = dp.checkIn;
            const salidaRegistrada = dp.checkOut;

            if (salidaProgramada === "00:00:00") {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    "24:00:00"
                  );
                } else if (entradaRegistrada < entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    "24:00:00"
                  );
                }
              }
            } else {
              if (entradaRegistrada !== salidaRegistrada) {
                if (entradaRegistrada >= entradaProgramada) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaRegistrada,
                    salidaProgramada
                  );
                } else if (
                  entradaRegistrada < entradaProgramada &&
                  salidaRegistrada > entradaProgramada
                ) {
                  hoursInSchedule = calcularTiempoTrabajado(
                    entradaProgramada,
                    salidaProgramada
                  );
                }
              }
            }
          }

          // Calcular el pago diario base
          const scheduleForPayment = {
            assistanceCheck_checkTime: dp.checkIn,
            assistanceCheckout_checkTime: dp.checkOut,
            schedule_startTime: dp.schedule?.startTime || "",
            schedule_endTime: dp.schedule?.endTime || "",
            incidents: dp.incidents || [],
            isCelebrityDayWorked: false, // No incluir el multiplicador aquí
            celebrityDay: null,
          };

          const dailyPayment = calculateDailyPayment(
            scheduleForPayment,
            dailySalary,
            hourlyPay,
            NormalCalculationNomina,
            hoursInSchedule,
            fullDayHours
          );
          
          // Para legacy, calcular solo el bono adicional (multiplicador - 1)
          if (dp.celebrityDay.bonusType) {
            const multiplierMatch = dp.celebrityDay.bonusType.match(/Salario x(\d+\.?\d*)/);
            if (multiplierMatch) {
              const multiplier = parseFloat(multiplierMatch[1]);
              // Solo sumar el bono adicional, no el salario completo
              const bonusAmount = dailyPayment * (multiplier - 1);
              total += bonusAmount;
            } else {
              total += dailyPayment;
            }
          } else {
            total += dailyPayment;
          }
        }
      }
    });

    return total;
  };

  const handleExportBulkRecibos = async () => {
    if (selectedPartnersForRecibo.length === 0) {
      notification(
        "Error",
        "Debes seleccionar al menos un colaborador",
        "warning"
      );
      return;
    }

    if (!branchSelected || !dates) {
      notification("Error", "Faltan datos necesarios", "danger");
      return;
    }

    setExporting(true);
    try {
      const startDate = dates.start.format("YYYY-MM-DD");
      const endDate = dates.end.format("YYYY-MM-DD");

      const payrollSummary = await findPayrollSummary(
        Number(branchSelected.id),
        startDate,
        endDate,
        1000,
        0,
        ""
      );

      const payrollMap = new Map<number, any>();
      (payrollSummary.payroll || []).forEach((record: any) => {
        payrollMap.set(record.partnerId, record);
      });

      // Map special partners by id for quick lookup
      const specialPartnersMap = new Map<number, { rawRecord: any; customDates: { start: dayjs.Dayjs; end: dayjs.Dayjs } }>();
      (specialPartners || []).forEach((sp) => {
        specialPartnersMap.set(sp.record.partnerId, { rawRecord: sp.rawRecord, customDates: sp.customDates });
      });

      let processedCount = 0;
      let errorCount = 0;
      let mainPdf: jsPDF | null = null;

      if (exportFormat === "excel") {
        const workbook = XLSX.utils.book_new();
        const usedExcelSheetNames = new Set<string>();

        for (const partnerId of selectedPartnersForRecibo) {
          try {
            const specialEntry = specialPartnersMap.get(partnerId);
            const isSpecial = Boolean(specialEntry);
            const payrollRecord = isSpecial ? specialEntry!.rawRecord : payrollMap.get(partnerId);
            if (!payrollRecord) {
              console.warn(`Partner ${partnerId} not found in payroll summary`);
              errorCount++;
              continue;
            }
            const effectiveStart = isSpecial ? specialEntry!.customDates.start.format("YYYY-MM-DD") : startDate;
            const effectiveEnd = isSpecial ? specialEntry!.customDates.end.format("YYYY-MM-DD") : endDate;
            const effectiveDates = isSpecial ? specialEntry!.customDates : dates;

            const { partnerData, paymentDetailData } =
              await loadPartnerReciboExportData(
                partnerId,
                Number(branchSelected.id),
                effectiveStart,
                effectiveEnd,
                payrollRecord,
                fullDayHours
              );

            const worksheet = await generateReciboSheet(
              partnerData,
              paymentDetailData,
              effectiveDates,
              Number(branchSelected.id),
              fullDayHours
            );

            const sheetName = buildUniqueExcelSheetName(
              partnerData.name,
              partnerId,
              usedExcelSheetNames
            );
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
            processedCount++;
          } catch (error) {
            console.error(`Error processing partner ${partnerId}:`, error);
            errorCount++;
          }
        }

        if (processedCount > 0) {
          const fileName = `Recibos_Nomina_${branchSelected.name}_${startDate}_${endDate}.xlsx`;
          XLSX.writeFile(workbook, fileName);
          notification(
            "Éxito",
            `${processedCount} recibos generados${
              errorCount > 0 ? `, ${errorCount} con errores` : ""
            }`,
            "success"
          );
        }
      } else {
        let receiptsInCurrentPage = 0;

        for (const partnerId of selectedPartnersForRecibo) {
          try {
            const specialEntry = specialPartnersMap.get(partnerId);
            const isSpecial = Boolean(specialEntry);
            const payrollRecord = isSpecial ? specialEntry!.rawRecord : payrollMap.get(partnerId);
            if (!payrollRecord) {
              console.warn(`Partner ${partnerId} not found in payroll summary`);
              errorCount++;
              continue;
            }
            const effectiveStart = isSpecial ? specialEntry!.customDates.start.format("YYYY-MM-DD") : startDate;
            const effectiveEnd = isSpecial ? specialEntry!.customDates.end.format("YYYY-MM-DD") : endDate;
            const effectiveDates = isSpecial ? specialEntry!.customDates : dates;

            const { partnerData, paymentDetailData } =
              await loadPartnerReciboExportData(
                partnerId,
                Number(branchSelected.id),
                effectiveStart,
                effectiveEnd,
                payrollRecord,
                fullDayHours
              );

            const generateReciboPDFPage = async (
              partnerData: any,
              paymentDetailData: any,
              dates: { start: dayjs.Dayjs; end: dayjs.Dayjs },
              branchId: number,
              pdfDoc: jsPDF | null,
              receiptPosition: "top" | "bottom" = "top"
            ): Promise<jsPDF> => {
              let currentPdf = pdfDoc;
              const { dailyPayments, paymentDetails, schedules } = paymentDetailData;
              const payment = paymentDetails;
              const effectiveSchedules = schedules || [];
              const NormalCalculationNomina = Boolean(
                partnerData.NormalCalculationNomina
              );

              let startDateDisplay = "N/A";
              let endDateDisplay = "N/A";
              let paymentDate = "N/A";
              if (dates?.start && dates?.end) {
                const startDate = dates.start.clone().add(3, "days");
                const endDate = dates.end.clone().add(3, "days");
                startDateDisplay = startDate.format("DD-MM-YYYY");
                endDateDisplay = endDate.format("DD-MM-YYYY");

                const startDay = startDate.date();
                const endDay = endDate.date();

                if (startDay <= 15 && endDay <= 15) {
                  paymentDate = "15";
                } else {
                  const lastDayOfMonth = endDate.clone().endOf("month").date();
                  paymentDate = lastDayOfMonth.toString();
                }
              }

              const parseCurrencyValue = (value: any): number => {
                if (typeof value === "number") return value;
                if (typeof value === "string") {
                  return Number(value.replace(/[^0-9.-]+/g, "") || 0) || 0;
                }
                return 0;
              };

              let chartSummary = {
                totalDays: 0,
                completeDays: 0,
                hourlyDays: 0,
                extraDays: 0,
                doubleShiftDays: 0,
              };
              let chartDetailMap = new Map<string, any>();
              let chartDaysData: any = { detailed: [] };

              try {
                const sd = dates.start.format("YYYY-MM-DD");
                const ed = dates.end.format("YYYY-MM-DD");
                const [chartResponse, incidentsResponse] = await Promise.all([
                  findChartDaysWorked(partnerData.partnerId, branchId, sd, ed),
                  findTotalIncidents(
                    partnerData.partnerId,
                    branchId,
                    sd,
                    ed
                  ).catch(() => ({ incidents: [] })),
                ]);
                const fetchedIncidents = incidentsResponse?.incidents || [];
                const adjustedDetailed = applyRejectedIncidentsZeroHorasExtras(
                  chartResponse.detailed || [],
                  fetchedIncidents
                );
                chartDaysData = {
                  detailed: adjustedDetailed,
                  graph: chartResponse.graph || [],
                };
                chartSummary = summarizeChartDays(chartDaysData.detailed || []);
                chartDaysData.detailed.forEach((item: any) => {
                  if (item?.fecha) {
                    chartDetailMap.set(item.fecha, item);
                  }
                });
              } catch (error) {
                console.error("Error fetching chart data:", error);
              }

              const getChartEntryForDailyPayment = (day: any) => {
                let dateStr = day.date;
                if (typeof dateStr === "string") {
                  const parsed = moment(
                    dateStr,
                    ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"],
                    true
                  );
                  if (parsed.isValid()) {
                    dateStr = parsed.format("DD-MM-YYYY");
                  } else {
                    dateStr = moment(dateStr).format("DD-MM-YYYY");
                  }
                } else {
                  dateStr = moment(dateStr).format("DD-MM-YYYY");
                }
                return chartDetailMap.get(dateStr);
              };

              const chartCompleteDates = new Set(
                (chartDaysData?.detailed || [])
                  .filter((item: any) => {
                    const hours = Number(item?.horasRegistradas ?? 0);
                    return hours >= fullDayHours && hours < fullDayHours * 2;
                  })
                  .map((item: any) => item.fecha)
              );

              const dailySalaryForPdf = parseCurrencyValue(
                partnerData.dailySalary
              );
              const hourlyPayForPdf = parseCurrencyValue(
                partnerData.payPerHour
              );

              let jornadasCompletasPuras = 0;
              let importeJornadasCompletas = 0;

              let pagosPorHoraCount = 0;
              let horasPagosPorHora = 0;
              let importePagosPorHora = 0;

              let horasExtrasCount = 0;
              let diasConExtras = 0;
              let importeHorasExtras = 0;

              let turnoDobleCount = 0;
              let importeTurnoDoble = 0;

              dailyPayments.forEach((day: any) => {
                const concept = (day.concept || "").toLowerCase();
                const hours = parseFloat(day.hours || "0");

                if (
                  isReciboRestDayWorkedRow(day) ||
                  isReciboRestDayEnjoyedRow(day)
                ) {
                  return;
                }

                if (
                  concept.includes("festivo trabajado") ||
                  concept.includes("día festivo")
                ) {
                  return;
                }

                if (concept.includes("doble jornada") || hours >= 16.5) {
                  turnoDobleCount++;
                  const paymentValue =
                    typeof day.payment === "number"
                      ? day.payment
                      : parseCurrencyValue(day.payment);
                  importeTurnoDoble += paymentValue;
                  return;
                }

                if (
                  hours > 0 &&
                  hours < fullDayHours &&
                  (concept.includes("pago por hora") ||
                    !concept.includes("jornada completa"))
                ) {
                  pagosPorHoraCount++;
                  let conceptHours = hours;
                  if (concept.includes("× pago por hora")) {
                    const match = concept.match(/(\d+(?:\.\d+)?)\s*h\s*×\s*pago por hora/i);
                    if (match) {
                      conceptHours = parseFloat(match[1]);
                    }
                  }
                  horasPagosPorHora += conceptHours;
                  return;
                }

                if (
                  concept.includes("jornada completa") &&
                  concept.includes("extras")
                ) {
                  jornadasCompletasPuras++;
                  importeJornadasCompletas += dailySalaryForPdf;
                  return;
                }

                if (concept.includes("jornada completa")) {
                  jornadasCompletasPuras++;
                  importeJornadasCompletas += dailySalaryForPdf;
                  return;
                }

                if (hours >= fullDayHours) {
                  jornadasCompletasPuras++;
                  importeJornadasCompletas += dailySalaryForPdf;
                }
              });

              const freeDaysWorkedFromRowsPdf = dailyPayments.filter((day: any) =>
                isReciboRestDayWorkedRow(day)
              ).length;
              const freeDaysWorked = Math.max(
                payment.freeDaysWorked || 0,
                freeDaysWorkedFromRowsPdf
              );
              // Calcular el importe directamente sumando los pagos de días de descanso trabajados
              const freeDayWorkedPayTotalNumber = dailyPayments
                .filter((day: any) => isReciboRestDayWorkedRow(day))
                .reduce((sum: number, day: any) => {
                  const paymentValue =
                    typeof day.payment === "number"
                      ? day.payment
                      : parseCurrencyValue(day.payment);
                  return sum + paymentValue;
                }, 0);
              const freeDaysEnjoyedFromRowsPdf = dailyPayments.filter((day: any) =>
                isReciboRestDayEnjoyedRow(day)
              ).length;
              const freeDaysEnjoyed = Math.max(
                payment.freeDaysEnjoyed || 0,
                freeDaysEnjoyedFromRowsPdf
              );
              const freeDaysEnjoyedPayNumber = freeDaysEnjoyed * dailySalaryForPdf;
              const totalIncentivesNumber = parseCurrencyValue(
                payment.totalIncentives
              );
              const celebrityDaysWorked = partnerData.celebrityDaysWorked || 0;
              // Calcular el total de días festivos trabajados desde schedules (igual que en PaymentDetail.tsx)
              // Usar schedules del backend en lugar de dailyPayments para tener la estructura completa
              let celebrityDaysPayTotalCalculated = 0;
              if (effectiveSchedules && effectiveSchedules.length > 0) {
                effectiveSchedules.forEach((schedule: any) => {
                  if (schedule.isCelebrityDayWorked && schedule.celebrityDay) {
                    const isNewSystem = schedule.celebrityDay.isNewSystem || false;
                    
                    if (isNewSystem && schedule.celebrityDay.amount) {
                      // Para nuevos bonos, usar el monto directamente
                      celebrityDaysPayTotalCalculated += Number(schedule.celebrityDay.amount) || 0;
                    } else {
                      // Para legacy, calcular basándose en bonusType
                      let hoursInScheduleForSchedule = 0;
                      if (
                        schedule.assistanceCheck_checkTime &&
                        schedule.assistanceCheckout_checkTime &&
                        schedule.schedule_startTime &&
                        schedule.schedule_endTime
                      ) {
                        const [start] = schedule.schedule_startTime.split(" - ");
                        const [, end] = schedule.schedule_endTime.split(" - ");
                        const entradaProgramada = start + ":00";
                        const salidaProgramada = end + ":00";
                        const entradaRegistrada = schedule.assistanceCheck_checkTime;
                        const salidaRegistrada = schedule.assistanceCheckout_checkTime;

                        if (salidaProgramada === "00:00:00") {
                          if (entradaRegistrada !== salidaRegistrada) {
                            if (entradaRegistrada >= entradaProgramada) {
                              hoursInScheduleForSchedule = calcularTiempoTrabajado(
                                entradaRegistrada,
                                "24:00:00"
                              );
                            } else if (entradaRegistrada < entradaProgramada) {
                              hoursInScheduleForSchedule = calcularTiempoTrabajado(
                                entradaProgramada,
                                "24:00:00"
                              );
                            }
                          }
                        } else {
                          if (entradaRegistrada !== salidaRegistrada) {
                            if (entradaRegistrada >= entradaProgramada) {
                              hoursInScheduleForSchedule = calcularTiempoTrabajado(
                                entradaRegistrada,
                                salidaProgramada
                              );
                            } else if (
                              entradaRegistrada < entradaProgramada &&
                              salidaRegistrada > entradaProgramada
                            ) {
                              hoursInScheduleForSchedule = calcularTiempoTrabajado(
                                entradaProgramada,
                                salidaProgramada
                              );
                            }
                          }
                        }
                      }

                      const dailyPayment = calculateDailyPayment(
                        schedule,
                        dailySalaryForPdf,
                        hourlyPayForPdf,
                        NormalCalculationNomina,
                        hoursInScheduleForSchedule,
                        fullDayHours
                      );
                      
                      // Para legacy, calcular solo el bono adicional (multiplicador - 1)
                      if (schedule.celebrityDay.bonusType) {
                        const multiplierMatch = schedule.celebrityDay.bonusType.match(/Salario x(\d+\.?\d*)/);
                        if (multiplierMatch) {
                          const multiplier = parseFloat(multiplierMatch[1]);
                          // Solo sumar el bono adicional, no el salario completo
                          const bonusAmount = dailyPayment * (multiplier - 1);
                          celebrityDaysPayTotalCalculated += bonusAmount;
                        } else {
                          celebrityDaysPayTotalCalculated += dailyPayment;
                        }
                      } else {
                        celebrityDaysPayTotalCalculated += dailyPayment;
                      }
                    }
                  }
                });
              }
              // Fallback al cálculo desde dailyPayments si no hay schedules
              if (celebrityDaysPayTotalCalculated === 0) {
                celebrityDaysPayTotalCalculated = calculateCelebrityDaysTotalFromBreakdown(
                  dailyPayments,
                  dailySalaryForPdf,
                  hourlyPayForPdf,
                  NormalCalculationNomina,
                  fullDayHours
                );
              }
              const celebrityDaysPayTotal =
                celebrityDaysPayTotalCalculated ||
                parseCurrencyValue(partnerData.celebrityDaysPayTotal || "0") ||
                0;

              // Calcular desde dailyPayments igual que en PaymentDetail.tsx:
              // totalDailyPayments ya incluye filas de descanso disfrutado.
              const hoursAuthorizedPayNumber = dailyPayments.reduce(
                (sum: number, day: any) => {
                  const paymentValue =
                    typeof day.payment === "number"
                      ? day.payment
                      : parseCurrencyValue(day.payment);
                  return sum + paymentValue;
                },
                0
              );

              const totalDeducciones = parseCurrencyValue(
                payment.totalDeductions
              );

              importePagosPorHora = horasPagosPorHora * hourlyPayForPdf; 
              horasExtrasCount = sumExtraHoursFromDailyPayments(
                dailyPayments,
                fullDayHours
              );
              diasConExtras = countDailyPaymentsWithAcceptedExtras(
                dailyPayments,
                fullDayHours
              );
              importeHorasExtras = horasExtrasCount * hourlyPayForPdf;

              const jornadasCuadro = getReciboJornadasCompletasCuadroDisplay(
                dailyPayments,
                fullDayHours
              );

              const ingresosData: (string | number)[][] = [];
              const horasDiasDescansoTrabajados =
                freeDaysWorked > 0 ? freeDaysWorked * fullDayHours : 0;
              const horasDiasDescansoDisfrutados =
                freeDaysEnjoyed > 0 ? freeDaysEnjoyed * fullDayHours : 0;
              const horasDiasFestivosTrabajados =
                celebrityDaysWorked > 0 ? celebrityDaysWorked * fullDayHours : 0;
              const horasDiasFestivos =
                partnerData.totalHollydays > 0
                  ? partnerData.totalHollydays * fullDayHours
                  : 0;

              const jornadasDias = jornadasCompletasPuras > 0
                ? jornadasCompletasPuras
                : jornadasCuadro.dias;
              const jornadasHoras = jornadasDias * fullDayHours;
              const jornadasImporte = jornadasDias * dailySalaryForPdf;

              ingresosData.push([
                "Jornadas completas",
                jornadasDias > 0 ? jornadasDias : "",
                jornadasDias > 0
                  ? jornadasHoras.toFixed(1)
                  : "",
                jornadasImporte > 0
                  ? getCurrencyFormat(jornadasImporte)
                  : "",
              ]);
              
              ingresosData.push([
                "Pagos por hora",
                pagosPorHoraCount > 0 ? pagosPorHoraCount : "",
                horasPagosPorHora > 0 ? horasPagosPorHora.toFixed(1) : "",
                importePagosPorHora > 0
                  ? getCurrencyFormat(horasPagosPorHora * hourlyPayForPdf)
                  : "",
              ]);
              ingresosData.push([
                "Horas extras",
                diasConExtras > 0 ? diasConExtras : "",
                horasExtrasCount > 0 ? horasExtrasCount.toFixed(1) : "",
                importeHorasExtras > 0
                  ? getCurrencyFormat(importeHorasExtras)
                  : "",
              ]);
              ingresosData.push([
                "Turno doble",
                turnoDobleCount > 0 ? turnoDobleCount : "",
                turnoDobleCount > 0 ? (turnoDobleCount * fullDayHours * 2).toFixed(1) : "",
                importeTurnoDoble > 0
                  ? getCurrencyFormat(importeTurnoDoble)
                  : "",
              ]);
              ingresosData.push([
                "Días de descanso trabajados",
                freeDaysWorked > 0 ? freeDaysWorked : "",
                horasDiasDescansoTrabajados > 0
                  ? horasDiasDescansoTrabajados.toFixed(1)
                  : "",
                freeDaysWorked > 0
                  ? getCurrencyFormat(freeDayWorkedPayTotalNumber)
                  : "",
              ]);
              ingresosData.push([
                "Días de descanso disfrutados",
                freeDaysEnjoyed > 0 ? freeDaysEnjoyed : "",
                horasDiasDescansoDisfrutados > 0
                  ? horasDiasDescansoDisfrutados.toFixed(1)
                  : "",
                freeDaysEnjoyed > 0 && freeDaysEnjoyedPayNumber > 0
                  ? getCurrencyFormat(freeDaysEnjoyedPayNumber)
                  : "",
              ]);
              ingresosData.push([
                "Bonos",
                "",
                "",
                totalIncentivesNumber > 0
                  ? getCurrencyFormat(totalIncentivesNumber)
                  : "",
              ]);
              ingresosData.push([
                "Días festivos trabajados",
                celebrityDaysWorked > 0 ? celebrityDaysWorked : "",
                horasDiasFestivosTrabajados > 0
                  ? horasDiasFestivosTrabajados.toFixed(1)
                  : "",
                celebrityDaysWorked > 0
                  ? getCurrencyFormat(celebrityDaysPayTotal)
                  : "",
              ]);
              ingresosData.push([
                "Días festivos",
                partnerData.totalHollydays > 0
                  ? partnerData.totalHollydays
                  : "",
                horasDiasFestivos > 0 ? horasDiasFestivos.toFixed(1) : "",
                partnerData.totalHollydays > 0
                  ? getCurrencyFormat(
                      partnerData.totalHollydays *
                        Number(
                          partnerData.dailySalary?.replace(/[^0-9.-]+/g, "") ||
                            0
                        )
                    )
                  : "",
              ]);
              ingresosData.push(["Incapacidades", "", "", ""]);
              ingresosData.push(["Aguinaldo", "", "", ""]);
              ingresosData.push(["Vacaciones", "", "", ""]);
              ingresosData.push(["Prima vacacional", "", "", ""]);
              ingresosData.push(["Otros", "", "", ""]);

              const mergedAbsenceDaily = mergeAbsenceRowsForRecibo(
                dailyPayments,
                paymentDetailData.absenceRows
              );
              const { rows: absenceOtrosRows, totalAmount: absenceOtrosTotal } =
                buildAbsenceOtrosIngresoRows(
                  mergedAbsenceDaily,
                  fullDayHours,
                  getCurrencyFormat
                );
              absenceOtrosRows.forEach((row) => ingresosData.push(row));

              const rawDeductionLines = collectReciboDeductionLines({
                deductionBreakdown: payment.deductionBreakdown,
                calculatedNewDeductions:
                  paymentDetailData.calculatedNewDeductions,
                calculatedMissingBoxes:
                  paymentDetailData.calculatedMissingBoxes,
              });
              if (
                partnerData.uniformDeduction &&
                parseCurrencyValue(partnerData.uniformDeduction) > 0
              ) {
                rawDeductionLines.push({
                  name: "Uniforme",
                  amount: parseCurrencyValue(partnerData.uniformDeduction),
                });
              }

              const groupedDeductions = groupDeductionsForRecibo(
                rawDeductionLines
              );
              let sumDetailed = groupedDeductions.reduce(
                (s, r) => s + r.amount,
                0
              );
              sumDetailed = Math.round(sumDetailed * 100) / 100;
              const totalRounded = Math.round(totalDeducciones * 100) / 100;

              const deduccionesData: string[][] = groupedDeductions.map(
                (row) => [row.displayName, getCurrencyFormat(row.amount)]
              );

              if (totalRounded > sumDetailed + 0.01) {
                deduccionesData.push([
                  "Otros",
                  getCurrencyFormat(totalRounded - sumDetailed),
                ]);
              } else if (groupedDeductions.length === 0 && totalRounded > 0) {
                deduccionesData.push([
                  "Otros",
                  getCurrencyFormat(totalRounded),
                ]);
              }

              const hollydaysTotal =
                partnerData.totalHollydays > 0
                  ? partnerData.totalHollydays *
                    parseCurrencyValue(partnerData.dailySalary)
                  : 0;

              const totalIngresos =
                (importeJornadasCompletas || 0) +
                (importePagosPorHora || 0) +
                (importeHorasExtras || 0) +
                (importeTurnoDoble || 0) +
                freeDayWorkedPayTotalNumber +
                freeDaysEnjoyedPayNumber +
                totalIncentivesNumber +
                celebrityDaysPayTotal +
                hollydaysTotal +
                absenceOtrosTotal;

              const totalAPagar =
                hoursAuthorizedPayNumber +
                totalIncentivesNumber +
                hollydaysTotal -
                totalDeducciones;
              const netoRecibido = totalAPagar;

              const maxRows = Math.max(
                ingresosData.length,
                deduccionesData.length
              );

              if (currentPdf === null) {
                currentPdf = new jsPDF();
              }

              const pageWidth = currentPdf.internal.pageSize.getWidth();
              const pageHeight = currentPdf.internal.pageSize.getHeight();

              const halfPageHeight = pageHeight / 2;

              let yPos: number;
              if (receiptPosition === "top") {
                yPos = 6;
              } else {
                const estimatedReceiptHeight = 95;

                const bottomHalfStart = halfPageHeight;
                yPos = bottomHalfStart + 2; // Pequeño margen después de la línea de media hoja
              }
              const leftMargin = 10;
              const rightMargin = pageWidth - 10;

              if (receiptPosition === "top") {
                currentPdf.setLineWidth(0.3);
                currentPdf.setDrawColor(150, 150, 150);

                const dashLength = 3;
                const gapLength = 2;
                let xPos = leftMargin;
                while (xPos < pageWidth - leftMargin) {
                  currentPdf.line(
                    xPos,
                    halfPageHeight,
                    Math.min(xPos + dashLength, pageWidth - leftMargin),
                    halfPageHeight
                  );
                  xPos += dashLength + gapLength;
                }
                currentPdf.setDrawColor(0, 0, 0);
              }
              const col1Width = 45;
              const col2Width = 12;
              const col3Width = 12;
              const col4Width = 25;
              const col5Start =
                leftMargin + col1Width + col2Width + col3Width + col4Width + 8;
              const col5Width = 45;
              const col6Width = 25;

              const drawRect = (
                x: number,
                y: number,
                w: number,
                h: number,
                color: number[]
              ) => {
                currentPdf.setFillColor(color[0], color[1], color[2]);
                currentPdf.rect(x, y, w, h, "F");
              };

              const drawTextWithBg = (
                text: string | number,
                x: number,
                y: number,
                options: any = {}
              ) => {
                const textStr = String(text || "").trim();
                if (!textStr || textStr === "undefined" || textStr === "null") {
                  return;
                }

                if (options.bgColor) {
                  const textWidth = currentPdf.getTextWidth(textStr);
                  drawRect(
                    x - 2,
                    y - (options.fontSize || 12) * 0.4,
                    textWidth + 4,
                    (options.fontSize || 12) * 0.6,
                    options.bgColor
                  );
                }

                if (options.textColor) {
                  currentPdf.setTextColor(
                    options.textColor[0],
                    options.textColor[1],
                    options.textColor[2]
                  );
                }

                currentPdf.setFontSize(options.fontSize || 12);
                currentPdf.setFont(
                  "helvetica",
                  options.bold ? "bold" : "normal"
                );

                try {
                  if (options.align === "center") {
                    currentPdf.text(textStr, x, y, { align: "center" });
                  } else {
                    currentPdf.text(textStr, x, y);
                  }
                } catch (err) {
                  console.error("Error drawing text:", textStr, x, y, err);

                  currentPdf.text(textStr, x, y);
                }

                if (options.textColor) {
                  currentPdf.setTextColor(0, 0, 0);
                }
              };

              const titleY = yPos;
              drawRect(
                leftMargin,
                titleY - 4,
                pageWidth - 20,
                6,
                [31, 78, 121]
              );
              drawTextWithBg("RECIBO DE NÓMINA", pageWidth / 2, titleY, {
                fontSize: 12,
                bold: true,
                textColor: [255, 255, 255],
                align: "center",
              });
              yPos += 6;

              const infoRows = [
                ["NOMBRE DEL EMPLEADO:", partnerData.name || ""],
                ["SUELDO BASE:", partnerData.salary || ""],
                ["PUESTO:", partnerData.position || ""],
                ["SUCURSAL:", partnerData.branch || ""],
                [
                  "PERIODO DE PAGO:",
                  `${startDateDisplay} al ${endDateDisplay}`,
                ],
                ["FECHA DE PAGO:", paymentDate],
              ];

              infoRows.forEach(([label, value]) => {
                drawRect(leftMargin, yPos - 2.5, col1Width, 4, [47, 85, 151]);
                drawTextWithBg(label, leftMargin + 1, yPos, {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                });
                drawRect(
                  leftMargin + col1Width,
                  yPos - 2.5,
                  pageWidth - 20 - col1Width,
                  4,
                  [248, 249, 250]
                );
                currentPdf.setFontSize(7);
                currentPdf.setFont("helvetica", "normal");
                currentPdf.text(value, leftMargin + col1Width + 1, yPos);
                yPos += 4;
              });

              yPos += 1.5;

              const tableStartY = yPos;
              drawRect(
                leftMargin,
                yPos - 2.5,
                col1Width + col2Width + col3Width + col4Width,
                5,
                [31, 78, 121]
              );
              drawTextWithBg(
                "INGRESOS",
                leftMargin +
                  (col1Width + col2Width + col3Width + col4Width) / 2,
                yPos,
                {
                  fontSize: 10,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(
                col5Start,
                yPos - 2.5,
                col5Width + col6Width,
                5,
                [31, 78, 121]
              );
              drawTextWithBg(
                "DEDUCCIONES",
                col5Start + (col5Width + col6Width) / 2,
                yPos,
                {
                  fontSize: 10,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              yPos += 5;

              drawRect(leftMargin, yPos - 2.5, col1Width, 4, [31, 78, 121]);
              drawTextWithBg("CONCEPTO", leftMargin + col1Width / 2, yPos, {
                fontSize: 7,
                bold: true,
                textColor: [255, 255, 255],
                align: "center",
              });
              drawRect(
                leftMargin + col1Width,
                yPos - 2.5,
                col2Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                "DÍAS",
                leftMargin + col1Width + col2Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(
                leftMargin + col1Width + col2Width,
                yPos - 2.5,
                col3Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                "HORAS",
                leftMargin + col1Width + col2Width + col3Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(
                leftMargin + col1Width + col2Width + col3Width,
                yPos - 2.5,
                col4Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                "IMPORTE",
                leftMargin + col1Width + col2Width + col3Width + col4Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(col5Start, yPos - 2.5, col5Width, 4, [31, 78, 121]);
              drawTextWithBg("CONCEPTO", col5Start + col5Width / 2, yPos, {
                fontSize: 7,
                bold: true,
                textColor: [255, 255, 255],
                align: "center",
              });
              drawRect(
                col5Start + col5Width,
                yPos - 2.5,
                col6Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                "IMPORTE",
                col5Start + col5Width + col6Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              yPos += 4;

              for (let i = 0; i < maxRows; i++) {
                const ingreso = ingresosData[i] || ["", "", "", ""];
                const deduccion = deduccionesData[i] || ["", ""];
                const isEvenRow = i % 2 === 0;
                const bgColor = isEvenRow ? [248, 249, 250] : [255, 255, 255];

                drawRect(leftMargin, yPos - 2, col1Width, 4, bgColor);
                currentPdf.setFontSize(6.5);
                currentPdf.setFont("helvetica", "normal");
                currentPdf.text(String(ingreso[0] || ""), leftMargin + 1, yPos);

                drawRect(
                  leftMargin + col1Width,
                  yPos - 2,
                  col2Width,
                  4,
                  bgColor
                );
                currentPdf.setFont("helvetica", "bold");
                currentPdf.text(
                  String(ingreso[1] || ""),
                  leftMargin + col1Width + col2Width / 2,
                  yPos,
                  { align: "center" }
                );

                drawRect(
                  leftMargin + col1Width + col2Width,
                  yPos - 2,
                  col3Width,
                  4,
                  bgColor
                );
                currentPdf.setFont("helvetica", "bold");
                currentPdf.text(
                  String(ingreso[2] || ""),
                  leftMargin + col1Width + col2Width + col3Width / 2,
                  yPos,
                  { align: "center" }
                );

                drawRect(
                  leftMargin + col1Width + col2Width + col3Width,
                  yPos - 2,
                  col4Width,
                  4,
                  bgColor
                );
                currentPdf.setFont("helvetica", "bold");
                currentPdf.text(
                  String(ingreso[3] || ""),
                  leftMargin +
                    col1Width +
                    col2Width +
                    col3Width +
                    col4Width / 2,
                  yPos,
                  { align: "center" }
                );

                drawRect(col5Start, yPos - 2, col5Width, 4, bgColor);
                currentPdf.setFont("helvetica", "normal");
                currentPdf.text(deduccion[0] || "", col5Start + 1, yPos);

                drawRect(
                  col5Start + col5Width,
                  yPos - 2,
                  col6Width,
                  4,
                  bgColor
                );
                currentPdf.setFont("helvetica", "bold");
                currentPdf.text(
                  deduccion[1] || "",
                  col5Start + col5Width + col6Width / 2,
                  yPos,
                  { align: "center" }
                );

                yPos += 4;
              }

              yPos += 1;
              drawRect(
                leftMargin,
                yPos - 2.5,
                col1Width + col2Width + col3Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                "INGRESOS TOTALES",
                leftMargin + (col1Width + col2Width + col3Width) / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(
                leftMargin + col1Width + col2Width + col3Width,
                yPos - 2.5,
                col4Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                getCurrencyFormat(totalIngresos),
                leftMargin + col1Width + col2Width + col3Width + col4Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(col5Start, yPos - 2.5, col5Width, 4, [31, 78, 121]);
              drawTextWithBg(
                "DEDUCCIONES TOTALES",
                col5Start + col5Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              drawRect(
                col5Start + col5Width,
                yPos - 2.5,
                col6Width,
                4,
                [31, 78, 121]
              );
              drawTextWithBg(
                getCurrencyFormat(totalDeducciones),
                col5Start + col5Width + col6Width / 2,
                yPos,
                {
                  fontSize: 7,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              yPos += 5;

              drawRect(
                leftMargin,
                yPos - 2.5,
                pageWidth - 20,
                5,
                [47, 85, 151]
              );
              drawTextWithBg(
                `PAGO NETO RECIBIDO: ${getCurrencyFormat(netoRecibido)}`,
                pageWidth / 2,
                yPos,
                {
                  fontSize: 9,
                  bold: true,
                  textColor: [255, 255, 255],
                  align: "center",
                }
              );
              yPos += 5.5;

              currentPdf.setFontSize(6.5);
              currentPdf.setFont("helvetica", "italic");
              currentPdf.text(
                "Recibí la cantidad anotada en este recibo en pago de mi sueldo.",
                leftMargin,
                yPos
              );
              yPos += 3;
              currentPdf.text(
                "Además certifico que no se me adeuda a la fecha, cantidad alguna por ningún concepto.",
                leftMargin,
                yPos
              );
              yPos += 3.5;
              currentPdf.setFont("helvetica", "normal");
              currentPdf.text("NOMBRE COMPLETO Y FIRMA:", leftMargin, yPos);
              yPos += 3;
              currentPdf.setLineWidth(0.5);
              currentPdf.line(leftMargin, yPos, leftMargin + 60, yPos);

              return currentPdf;
            };

            const receiptPosition: "top" | "bottom" =
              receiptsInCurrentPage === 0 ? "top" : "bottom";

            if (receiptPosition === "top" && mainPdf !== null) {
              mainPdf.addPage();
              receiptsInCurrentPage = 0;
            }

            mainPdf = await generateReciboPDFPage(
              partnerData,
              paymentDetailData,
              effectiveDates,
              Number(branchSelected.id),
              mainPdf,
              receiptPosition
            );

            receiptsInCurrentPage++;

            if (receiptsInCurrentPage >= 2) {
              receiptsInCurrentPage = 0;
            }

            processedCount++;
          } catch (error) {
            console.error(`Error processing partner ${partnerId}:`, error);
            errorCount++;
          }
        }

        if (processedCount > 0 && mainPdf !== null) {
          const fileName = `Recibos_Nomina_${branchSelected.name}_${startDate}_${endDate}.pdf`;
          mainPdf.save(fileName);
          notification(
            "Éxito",
            `${processedCount} recibos generados${
              errorCount > 0 ? `, ${errorCount} con errores` : ""
            }`,
            "success"
          );
        }
      }

      if (processedCount > 0) {
        onClose();
        setSelectedPartnersForRecibo([]);
        setSelectAllMode(false);
        setSearchPartner("");
      } else {
        notification("Error", "No se pudo generar ningún recibo", "danger");
      }
    } catch (error) {
      console.error("Error exporting bulk recibos:", error);
      notification("Error", "Error al generar los recibos", "danger");
    } finally {
      setExporting(false);
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedPartnersForRecibo([]);
    setSelectAllMode(false);
    setSearchPartner("");
    setShowPartnerDropdown(false);
  };

  return (
    <ModalContainer
      open={open}
      title="Descargar Recibos Masivos"
      closeFn={handleClose}
      size="lg"
    >
      <div style={{ minHeight: "100px" }}>
        {/* Información de sucursal y período */}
        <div className="card bg-light mb-3">
          <div className="card-body py-2">
            <div className="row">
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <i className="fa fa-building me-2 text-primary"></i>
                  <div>
                    <small className="text-muted d-block">Sucursal</small>
                    <strong className="text-dark">
                      {branchSelected?.name || "No seleccionada"}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="d-flex align-items-center">
                  <i className="fa fa-calendar me-2 text-primary"></i>
                  <div>
                    <small className="text-muted d-block">Período</small>
                    <strong className="text-dark">
                      {dates
                        ? `${dates.start.format(
                            "DD [de] MMMM [de] YYYY"
                          )} - ${dates.end.format("DD [de] MMMM [de] YYYY")}`
                        : "No definido"}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">Colaborador</label>
          <div className="position-relative" ref={searchContainerRef}>
            <div className="input-group">
              <div
                className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
                style={{ zIndex: 10 }}
              >
                {searchLoading ? (
                  <i className="fa fa-spinner fa-spin" />
                ) : (
                  <i className="fa fa-users opacity-5" />
                )}
              </div>
              <input
                type="text"
                className="form-control px-35px bg-light"
                placeholder={
                  selectedPartnersForRecibo.length > 0
                    ? `Buscar colaboradores... (${selectedPartnersForRecibo.length} seleccionados)`
                    : "Buscar colaborador..."
                }
                value={searchPartner}
                onChange={(e) => handlePartnerSearch(e.target.value)}
                onFocus={() => {
                  setShowPartnerDropdown(true);
                  if (
                    searchPartner.trim() === "" &&
                    branchSelected &&
                    filteredPartners.length === 0
                  ) {
                    handlePartnerSearch("");
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setShowPartnerDropdown(false);
                }}
              />
            </div>
            {showPartnerDropdown && (
              <div
                className="position-absolute w-100 bg-white border rounded shadow-sm"
                style={{
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {/* Mostrar primero los seleccionados */}
                {selectedPartnersForRecibo.length > 0 &&
                  (() => {
                    const selectedPartnersList = selectedPartnersForRecibo
                      .map((id) => partners.find((p) => p.id === id))
                      .filter((p): p is Partner => p !== undefined);

                    if (selectedPartnersList.length > 0) {
                      return (
                        <>
                          <div className="px-3 py-2 border-bottom bg-light">
                            <small className="text-muted fw-bold">
                              <i className="fa fa-check-circle me-1 text-success"></i>
                              Seleccionados ({selectedPartnersList.length})
                            </small>
                          </div>
                          {selectedPartnersList.map((partner) => (
                            <div
                              key={partner.id}
                              className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center partner-dropdown-item"
                              style={{
                                cursor: "pointer",
                                backgroundColor: "#e3f2fd",
                              }}
                              onClick={() => handlePartnerToggle(partner)}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#d1e7dd")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#e3f2fd")
                              }
                            >
                              <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {}}
                                className="me-2"
                                style={{ width: 16, height: 16 }}
                              />
                              <i className="fa fa-user me-2 text-success"></i>
                              {partner.person.firstName}{" "}
                              {partner.person.lastName}
                            </div>
                          ))}
                        </>
                      );
                    }
                    return null;
                  })()}

                {/* Botón de seleccionar todos */}
                {branchSelected &&
                  (() => {
                    const allFilteredSelected =
                      filteredPartners.length > 0
                        ? filteredPartners.every((partner) =>
                            selectedPartnersForRecibo.includes(partner.id)
                          )
                        : false;

                    return (
                      <>
                        {selectedPartnersForRecibo.length > 0 && (
                          <div className="px-3 py-2 border-top border-bottom bg-light">
                            <small className="text-muted fw-bold">
                              <i className="fa fa-users me-1"></i>
                              {searchPartner.trim() === ""
                                ? "Colaboradores disponibles"
                                : "Resultados de búsqueda"}
                            </small>
                          </div>
                        )}
                        <div
                          className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center partner-dropdown-item"
                          style={{
                            cursor: "pointer",
                            backgroundColor: allFilteredSelected
                              ? "#e3f2fd"
                              : "white",
                          }}
                          onClick={handleSelectAllPartners}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              allFilteredSelected ? "#e3f2fd" : "#f8f9fa")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor =
                              allFilteredSelected ? "#e3f2fd" : "white")
                          }
                        >
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={() => {}}
                            className="me-2"
                            style={{ width: 16, height: 16 }}
                          />
                          <strong
                            className={
                              allFilteredSelected
                                ? "text-warning"
                                : "text-primary"
                            }
                          >
                            <i
                              className={`fa ${
                                allFilteredSelected ? "fa-minus" : "fa-plus"
                              } me-1`}
                            ></i>
                            {allFilteredSelected
                              ? `Deseleccionar todos (${selectedPartnersForRecibo.length} seleccionados)`
                              : `Agregar todos de la sucursal${
                                  selectedPartnersForRecibo.length > 0
                                    ? ` (${selectedPartnersForRecibo.length} seleccionados)`
                                    : ""
                                }`}
                          </strong>
                        </div>
                      </>
                    );
                  })()}

                {/* Mostrar los 10 partners por defecto o resultados de búsqueda */}
                {filteredPartners.length > 0 && (
                  <>
                    {filteredPartners.map((partner) => {
                      if (selectedPartnersForRecibo.includes(partner.id)) {
                        return null;
                      }
                      return (
                        <div
                          key={partner.id}
                          className="px-3 py-2 cursor-pointer hover-bg-light d-flex align-items-center partner-dropdown-item"
                          style={{
                            cursor: "pointer",
                            backgroundColor: "white",
                          }}
                          onClick={() => handlePartnerToggle(partner)}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f8f9fa")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "white")
                          }
                        >
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => {}}
                            className="me-2"
                            style={{ width: 16, height: 16 }}
                          />
                          <i className="fa fa-user me-2 text-info"></i>
                          {partner.person.firstName} {partner.person.lastName}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {selectedPartnersForRecibo.length > 0 && (
          <div className="alert alert-info mb-3">
            <i className="fa fa-info-circle me-2"></i>
            {selectedPartnersForRecibo.length} colaborador
            {selectedPartnersForRecibo.length > 1 ? "es" : ""} seleccionado
            {selectedPartnersForRecibo.length > 1 ? "s" : ""}
            {selectedPartnersForRecibo.length > 0 && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => {
                  setSelectedPartnersForRecibo([]);
                  setSelectAllMode(false);
                }}
                title="Limpiar todos los colaboradores"
              >
                <i className="fa fa-trash me-1"></i>
                Limpiar
              </button>
            )}
          </div>
        )}

        {/* Información adicional */}
        <div className="card border mb-3">
          <div className="card-body py-2">
            <div className="row text-center">
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-link p-0 w-100 text-decoration-none"
                  onClick={() => setExportFormat("excel")}
                  style={{
                    border:
                      exportFormat === "excel"
                        ? "2px solid #28a745"
                        : "2px solid transparent",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor:
                      exportFormat === "excel" ? "#f0f9f4" : "transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <i className="fa fa-file-excel fa-2x text-success mb-2"></i>
                    <small className="text-muted">Formato</small>
                    <strong className="text-dark">Excel (.xlsx)</strong>
                  </div>
                </button>
              </div>
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-link p-0 w-100 text-decoration-none"
                  onClick={() => setExportFormat("pdf")}
                  style={{
                    border:
                      exportFormat === "pdf"
                        ? "2px solid #dc3545"
                        : "2px solid transparent",
                    borderRadius: "8px",
                    padding: "12px",
                    backgroundColor:
                      exportFormat === "pdf" ? "#fff5f5" : "transparent",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div className="d-flex flex-column align-items-center">
                    <i className="fa fa-file-pdf fa-2x text-danger mb-2"></i>
                    <small className="text-muted">Formato</small>
                    <strong className="text-dark">PDF (.pdf)</strong>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Consejos e información */}
        <div className="alert alert-info mb-3">
          <div className="d-flex align-items-start">
            <i className="fa fa-info-circle me-2 mt-1"></i>
            <div>
              <strong>Consejos:</strong>
              <ul className="mb-0 mt-2">
                <li>
                  <strong>Búsqueda:</strong> Escribe el nombre del colaborador
                  para buscar. Se muestran 10 colaboradores por defecto cuando
                  el campo está vacío
                </li>
                <li>
                  <strong>Agregar todos:</strong> Usa "Agregar todos de la
                  sucursal" para seleccionar todos los colaboradores de la
                  sucursal actual
                </li>
                <li>
                  <strong>Exportar:</strong> Una vez seleccionados los
                  colaboradores, haz clic en "Descargar" para generar el archivo
                  Excel con los recibos
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={exporting}
          >
            Cancelar
          </button>
          <button
            className="btn btn-success"
            onClick={handleExportBulkRecibos}
            disabled={selectedPartnersForRecibo.length === 0 || exporting}
          >
            {exporting ? (
              <>
                <i className="fa fa-spinner fa-spin me-2"></i>
                Generando...
              </>
            ) : (
              <>
                <i className="fas fa-file-excel me-2"></i>
                Descargar{" "}
                {selectedPartnersForRecibo.length > 0
                  ? `(${selectedPartnersForRecibo.length})`
                  : ""}
              </>
            )}
          </button>
        </div>
      </div>
    </ModalContainer>
  );
};

export default BulkRecibosModal;
